/**
 * Virtual Tour Block
 *
 * Embeds virtual tours from the property's virtual_tours collection.
 * Displays a tour selector dropdown when multiple tours are available.
 * All text content is parameterized via block tokens for per-instance editing.
 *
 * Inherits theme styling through CSS variables and Tailwind utility classes.
 */

import { useState } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, EditableBlockToken } from '@vibesites/runtime/react';
import { useProperty } from '@vibesites/runtime/react';
import { defineComponentRequirements, collections } from '@vibesites/tokens';
import { cn } from '@vibesites/ui-primitives';
import type { VirtualTour as VirtualTourType } from '@vibesites/schema';

// ============================================================================
// Sub-components
// ============================================================================

function TourSelector({
  tours,
  selectedIndex,
  onSelect,
}: {
  tours: VirtualTourType[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}) {
  return (
    <div className="relative">
      <select
        value={selectedIndex}
        onChange={(e) => onSelect(Number(e.target.value))}
        className={cn(
          'appearance-none cursor-pointer',
          'bg-primary text-primary-foreground',
          'px-4 py-2.5 pr-10 text-sm font-medium',
          'rounded-md shadow-md',
          'min-w-[200px]',
          'transition-all duration-200',
          'hover:bg-primary/90',
          'focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2'
        )}
      >
        {tours.map((tour, index) => (
          <option key={index} value={index} className="bg-background text-foreground">
            {tour.virtual_tour_title || `Virtual Tour ${index + 1}`}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-foreground">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>
  );
}

function TourEmbed({ tour }: { tour: VirtualTourType }) {
  const [isLoading, setIsLoading] = useState(true);

  if (!tour.virtual_tour_url) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-lg bg-muted shadow-lg">
        <div className="text-center">
          <div className="mb-3 text-4xl font-light text-muted-foreground/50">360°</div>
          <p className="text-muted-foreground">Tour URL not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-lg bg-muted shadow-lg">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="flex flex-col items-center gap-3">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-sm text-muted-foreground">Loading virtual tour...</p>
          </div>
        </div>
      )}
      <iframe
        src={tour.virtual_tour_url}
        title={tour.virtual_tour_title || 'Virtual Tour'}
        className="aspect-video w-full border-0"
        allow="vr; xr; accelerometer; magnetometer; gyroscope; autoplay; fullscreen"
        allowFullScreen
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

function VirtualTourComponent({ sectionId: _sectionId }: BlockProps) {
  const property = useProperty();
  const virtualTours = (property.virtual_tours || []).filter(
    (tour): tour is VirtualTourType => tour !== null && tour !== undefined
  );

  const title = useBlockToken('title', { defaultValue: 'Virtual Tour' });
  const subtitle = useBlockToken('subtitle', {
    defaultField: 'property_virtualtours_blurb',
    defaultValue: '',
  });
  const showHeader = useBlockToken('show_header', { defaultValue: 'true' });
  const showSelector = useBlockToken('show_selector', { defaultValue: 'true' });

  const [selectedIndex, setSelectedIndex] = useState(0);

  // Empty state
  if (virtualTours.length === 0) {
    return (
      <div className="py-16 text-center bg-muted" style={{ color: 'var(--vibe-color-text-muted)' }}>
        <div className="mx-auto max-w-md">
          <div className="mb-4 text-5xl font-light" style={{ opacity: 0.4 }}>360°</div>
          <EditableBlockToken
            localName="title"
            label="Section Title"
            defaultValue="Virtual Tour"
            as="h3"
            className="mb-2 text-lg font-medium"
          >
            {title || 'Virtual Tour'}
          </EditableBlockToken>
          <p className="text-sm">
            No virtual tours are available for this property.
          </p>
        </div>
      </div>
    );
  }

  const selectedTour = virtualTours[Math.min(selectedIndex, virtualTours.length - 1)];
  const hasMultipleTours = virtualTours.length > 1;
  const headerVisible = showHeader !== 'false';
  const selectorVisible = showSelector !== 'false';

  return (
    <section className="px-4 py-8 md:px-6 md:py-12 lg:px-8 lg:py-16 bg-muted">
      <div className="mx-auto max-w-6xl">
        {headerVisible && (title || subtitle) && (
          <div className="mb-8 text-center">
            {title && (
              <EditableBlockToken
                localName="title"
                label="Section Title"
                defaultValue="Virtual Tour"
                as="h2"
                className="text-2xl font-bold tracking-tight md:text-3xl"
                style={{ color: 'var(--vibe-color-text)', fontFamily: 'var(--vibe-font-heading)' }}
              >
                {title}
              </EditableBlockToken>
            )}
            {subtitle && (
              <EditableBlockToken
                localName="subtitle"
                label="Section Subtitle"
                defaultField="property_virtualtours_blurb"
                as="p"
                className="mt-3 max-w-xl mx-auto"
                style={{ color: 'var(--vibe-color-text-muted)' }}
              >
                {subtitle}
              </EditableBlockToken>
            )}
          </div>
        )}

        {hasMultipleTours && selectorVisible && (
          <div className="mb-6 flex justify-end">
            <TourSelector
              tours={virtualTours}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
            />
          </div>
        )}

        <TourEmbed tour={selectedTour} />
      </div>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourBlock',
  displayName: 'Virtual Tour',
  tokens: {
    title: {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Virtual Tour',
      description: 'The headline for the virtual tour section.',
    },
    subtitle: {
      label: 'Section Subtitle',
      type: 'text',
      defaultField: 'property_virtualtours_blurb',
      description: 'The description text below the title.',
      suggestedFields: [
        'property_virtualtours_blurb',
        'property_short_description',
      ],
    },
    show_header: {
      label: 'Show Header',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the title and subtitle.',
    },
    show_selector: {
      label: 'Show Tour Selector',
      type: 'text',
      defaultValue: 'true',
      description: 'Set to "false" to hide the tour dropdown when multiple tours exist.',
    },
  },
};

// ============================================================================
// Block Definition
// ============================================================================

export const virtualTourBlock: BlockDefinition = {
  id: 'virtual-tour',
  name: 'Virtual Tour',
  description: 'Embed virtual tours from the property data with an optional tour selector dropdown.',
  category: 'Media',
  component: VirtualTourComponent,
  contentSchema,
  requirements: defineComponentRequirements('VirtualTourBlock', {
    designTokens: [
      'color.primary',
      'color.surface',
      'color.text',
      'color.textMuted',
      'color.border',
      'radius.lg',
    ],
    dataTokens: [
      collections.required('virtual_tours', 'Virtual tour URLs and titles'),
    ],
  }),
  tags: ['virtual-tour', '360', 'iframe', 'tour', 'embed', 'media'],
  defaultGridSpan: 12,
};
